import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core'
import { apolloClient, isServerDownError, type DocumentNode } from '@/vendor/apollo-client.ts'

// Routes registered catalog reads to the local replica when the gate allows
// (offline + entitled), else to Apollo. Unregistered ops always hit the network,
// so a half-built replica never breaks a screen. The gate is injected by the
// catalog-sync store to keep this module free of store imports (no cycle).
type CatalogHandler = (variables: Record<string, unknown>) => Promise<{ data: unknown }>

const registry = new Map<DocumentNode, CatalogHandler>()
let replicaGate: (document: DocumentNode) => boolean = () => false

export function registerCatalogOp<T extends DocumentNode>(
  document: T,
  handler: (variables: VariablesOf<T>) => Promise<{ data: ResultOf<T> }>,
): void {
  registry.set(document, handler as CatalogHandler)
}

// For the generated CATALOG_OPS loop, where the document is a plain DocumentNode
// and variables/result types can't be statically related per iteration.
export function registerCatalogOpLoose(document: DocumentNode, handler: CatalogHandler): void {
  registry.set(document, handler)
}

export function setReplicaGate(gate: (document: DocumentNode) => boolean): void {
  replicaGate = gate
}

export async function catalogQuery<T extends DocumentNode>(options: {
  query: T
  variables: VariablesOf<T>
  fetchPolicy?: 'cache-first' | 'network-only' | 'cache-only' | 'no-cache' | 'standby'
}): Promise<{ data: ResultOf<T> }> {
  const handler = registry.get(options.query)
  if (handler && replicaGate(options.query)) {
    return handler(options.variables as Record<string, unknown>) as Promise<{
      data: ResultOf<T>
    }>
  }
  try {
    const result = await apolloClient.query(options)
    return { data: result.data as ResultOf<T> }
  } catch (error) {
    // The server dropped mid-request. The gate flips to the replica only after the
    // outage is detected, so re-check it here and serve this read locally when the
    // user is entitled — otherwise the outage surfaces as an error/overlay.
    if (handler && isServerDownError(error) && replicaGate(options.query)) {
      return handler(options.variables as Record<string, unknown>) as Promise<{
        data: ResultOf<T>
      }>
    }
    throw error
  }
}
